import React, { useEffect, useRef } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material";
import { BookingGetters } from "../../../store/onlineBooking/getters";
import "./VehicleAvailabilityLoader.css";

interface VehicleAvailabilityLoaderProps {
  isLoading?: boolean;
  onComplete?: () => void;
}
import { TextResource } from "../../../@types/DTO/AppSetting";

export function VehicleAvailabilityLoader({
  isLoading = true,
  onComplete,
}: VehicleAvailabilityLoaderProps = {}) {
  const { t: translate } = useTranslation();
  const theme = useTheme();
  const appSettings = BookingGetters.getAppSettings();

  const prevLoadingRef = useRef<boolean>(isLoading);
  const finishStartedRef = useRef<boolean>(false);
  const onCompleteRef =
    useRef<VehicleAvailabilityLoaderProps["onComplete"]>(onComplete);
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = isLoading;

    if (!wasLoading || isLoading || finishStartedRef.current) return;
    finishStartedRef.current = true;

    // No progress illusion to "catch up" to anymore — the car loader is
    // indeterminate, so it can disappear the instant the API responds.
    onCompleteRef.current?.();
  }, [isLoading]);
  const primary =
    theme?.palette?.primary?.main || appSettings?.primaryColor || "#9ca3af";

  const lang = i18n.language;
  const shortLang = lang?.split("-")[0];
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const langEntries =
    textResource?.[lang] ||
    (shortLang && shortLang !== lang ? textResource?.[shortLang] : undefined);

  const data: TextResource[] =
    langEntries?.flatMap((item: any) => item.data || []) || [];

  const loaderTextEntry = data.find(
    (item: TextResource) => item.key === "enableCustomLoaderText"
  );
  const titleText =
    loaderTextEntry?.body || translate("We're loading the available cars...");
  const descriptionText =
    loaderTextEntry?.description ||
    translate("One moment, we're looking for the best options for you.");

  return (
    <div
      className="flex flex-col justify-center items-center w-full"
      style={
        {
          "--vehicle-loader-color": primary,
          padding: "56px 16px",
        } as React.CSSProperties
      }
      role="status"
      aria-live="polite"
    >
      <div className="vehicle-loader-inner">
        <div className="vehicle-loader-car-wrap">
          <svg
            className="vehicle-loader-car"
            viewBox="0 0 120 72"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M 12 52 L 12 42 Q 14 34 24 32 L 36 30 L 46 16 Q 48 12 54 12 L 72 12 C 92 12 108 28 108 44 L 108 52 L 97 52 A 7 7 0 0 1 83 52 L 37 52 A 7 7 0 0 1 23 52 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            <path
              d="M 36 30 L 102 30"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
            {/* wheels */}
            <circle
              cx="30"
              cy="56"
              r="8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            />
            <circle
              cx="90"
              cy="56"
              r="8"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            />
          </svg>
        </div>

        <div className="vehicle-loader-dots" aria-hidden="true">
          <span className="vehicle-loader-dot" />
          <span className="vehicle-loader-dot" />
          <span className="vehicle-loader-dot" />
        </div>

        <div className="vehicle-loader-copy">
          <div className="vehicle-loader-title">{titleText}</div>
          <div className="vehicle-loader-description">{descriptionText}</div>
        </div>
      </div>
    </div>
  );
}

export default VehicleAvailabilityLoader;
